'use client';
import type { IHeaderMenu } from './data';

import React from 'react';
import { useParams, usePathname } from 'next/navigation';

import {
  BoardLinks,
  Box,
  Button,
  Container,
  DesktopCart,
  Divider,
  Flex,
  HamburgerMenu,
  HeaderProfile,
  HeaderSearch,
  HStack,
  Icon,
  Image,
  Link as CkLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
} from '@/components';
import { useCategories, useTranslation } from '@/hooks';
import useBrands from '@/hooks/queries/useBrands';
import { routes, toolsLinks } from '@/statics';
import ArrowDown from '@/svgs/ArrowDown.svg';
import LiateamLogo from '@/svgs/liateam-logo.svg';

const HeaderDesktop: IHeaderMenu = () => {
  const { t } = useTranslation('components.header');
  const location = useParams();
  const pathname = usePathname();

  const { data: categoriesData } = useCategories();
  const { data: brands } = useBrands();

  const categoriesTree = categoriesData
    ?.filter((item) => item.parent_code === null)
    .map((item) => ({
      ...item,
      children: categoriesData.filter((el) => el.parent_code === item.code),
    }));

  const [category, setCategory] = React.useState(categoriesTree?.[0].id);
  const [brand, setBrand] = React.useState(brands?.[0].title);
  const internalLinks = [
    { title: routes.blogs.title, url: routes.blogs.url },
    { title: routes.executiveTeam.title, url: routes.executiveTeam.url },
    { title: routes.aboutUs.title, url: routes.aboutUs.url },
    { title: routes.contactUs.title, url: routes.contactUs.url },
    { title: routes.faq.title, url: routes.faq.url },
  ];

  return (
    <Box background="white" zIndex="docked" position="relative">
      <Container paddingInline={0} display={{ md: 'block', base: 'none' }}>
        <Stack pb={2.5} gap={2.5}>
          <Flex gap="42px" pb={2.5} bg="secondary.50" px={2.5} py="22px">
            {toolsLinks.map((toolLink) => (
              <CkLink
                prefetch={false}
                target="_blank"
                fontSize="sm"
                color="primary.400"
                key={toolLink.url}
                href={toolLink.url || ''}
              >
                {toolLink.title}
              </CkLink>
            ))}
            <Menu>
              <MenuButton
                fontSize="sm"
                fontWeight={400}
                role="group"
                color="primary.400"
                as={Button}
                alignItems="center"
                display="inline-flex"
                p={0}
                height="18px"
                rightIcon={
                  <Flex alignItems="center">
                    <ArrowDown fill="primary.400" />
                  </Flex>
                }
                variant="unstyled"
              >
                {t('liaBoards')}
              </MenuButton>
              <MenuList
                display="flex"
                borderRadius={4}
                zIndex="dropdown"
                pb={8}
                w="300px"
              >
                <BoardLinks />
              </MenuList>
            </Menu>
          </Flex>
          <Flex
            gap={6}
            px={2.5}
            justify="space-between"
            align="center"
            h="69px"
          >
            <Flex gap={6} align="flex-end" alignItems="center">
              <CkLink href="/" display="flex">
                <Icon w="147px" h="49px" as={LiateamLogo} />
              </CkLink>
              <Box display={{ base: 'none', md: 'block', xl: 'none' }}>
                <HamburgerMenu />
              </Box>
              <Box display={{ base: 'none', xl: 'flex' }}>
                <Flex align="center">
                  <CkLink
                    color={
                      pathname === routes.categories.url
                        ? 'secondary.600'
                        : 'text'
                    }
                    fontWeight={500}
                    href={routes.categories.url}
                    height="min-content"
                    px={5}
                    ps={3}
                    _hover={{ color: 'secondary.600' }}
                  >
                    {t('shops')}
                  </CkLink>
                </Flex>
                <Menu onClose={() => setCategory(categoriesTree?.[0].id)}>
                  <MenuButton
                    role="group"
                    _hover={{ color: 'secondary.600' }}
                    as={Button}
                    alignItems={'center'}
                    display="inline-flex"
                    px={5}
                    color={
                      pathname.startsWith(`${routes.categories.url}/`) &&
                      pathname !== routes.categories.url
                        ? 'secondary.600'
                        : 'text'
                    }
                    rightIcon={
                      <Flex
                        _groupHover={{ fill: 'secondary.600' }}
                        fill={
                          pathname.startsWith(`${routes.categories.url}/`) &&
                          pathname !== routes.categories.url
                            ? 'secondary.600'
                            : 'text'
                        }
                        alignItems="center"
                      >
                        <ArrowDown fill={'text'} />
                      </Flex>
                    }
                    fontWeight={500}
                    variant="unstyled"
                  >
                    {t('categories')}
                  </MenuButton>
                  <MenuList
                    margin={0}
                    padding={0}
                    display={'flex'}
                    borderRadius={'11px'}
                    zIndex={'dropdown'}
                    height={
                      categoriesTree && `${categoriesTree?.length * 49 + 50}px`
                    }
                    minHeight={'318px'}
                    w="100%"
                  >
                    <Box margin={0} padding={0} pt={'21px'}>
                      {categoriesTree?.map((item) => (
                        <MenuItem
                          as={CkLink}
                          key={item.id}
                          href={routes.category.href(item.code)}
                          paddingRight={'10px'}
                          w={'240px'}
                          mr={'36px'}
                          pt={'12px'}
                          pb={'13px'}
                          color={'text'}
                          bgColor={
                            item.id === category
                              ? 'secondary.100'
                              : 'transparent'
                          }
                          cursor={'pointer'}
                          onMouseEnter={() => setCategory(item.id)}
                        >
                          <Box mr={'10px'}>{item.title}</Box>
                        </MenuItem>
                      ))}
                    </Box>
                    <Box
                      minHeight="318px"
                      height={
                        categoriesTree &&
                        `${categoriesTree?.length * 45 + 50}px`
                      }
                      paddingBlock="21px"
                    >
                      <Divider orientation="vertical" />
                    </Box>
                    <Stack
                      direction="column"
                      flexWrap="wrap"
                      margin={0}
                      padding={0}
                      pt={'21px'}
                      minWidth="400px"
                      gap="0"
                    >
                      {categoriesTree?.map(
                        (item) =>
                          item?.id === category &&
                          item?.children.map((sub) => (
                            <MenuItem
                              as={CkLink}
                              sx={
                                location.subcategory === sub.id
                                  ? { bg: 'primary.100', color: 'white' }
                                  : {}
                              }
                              href={routes.subCategory.href(
                                item.code,
                                sub.code
                              )}
                              key={sub.id}
                              paddingTop={'12px'}
                              paddingBottom={'13px'}
                              mr={'19px'}
                              w={'204px'}
                              color={'text'}
                              fontSize={'sm'}
                            >
                              {sub.title}
                            </MenuItem>
                          ))
                      )}
                    </Stack>
                    <Box
                      height="auto"
                      aspectRatio={523 / 960}
                      margin="21px 21px 21px 23px"
                      display={'flex'}
                      position="relative"
                      alignItems={'center'}
                      sx={{
                        img: {
                          borderRadius: '8px',
                          objectFit: 'cover',
                        },
                      }}
                    >
                      <Image
                        src={
                          categoriesTree?.find((item) => item.id === category)
                            ?.banner_image || ''
                        }
                        alt="categories"
                        fill
                      />
                    </Box>
                  </MenuList>
                </Menu>
                {brands && brands?.length > 0 && (
                  <Menu onClose={() => setBrand(brands?.[0].title)}>
                    <MenuButton
                      role="group"
                      _hover={{ color: 'secondary.600' }}
                      as={Button}
                      alignItems="center"
                      display="inline-flex"
                      px={5}
                      color={
                        pathname.startsWith('/brands')
                          ? 'secondary.600'
                          : 'text'
                      }
                      rightIcon={
                        <Flex
                          _groupHover={{ fill: 'secondary.600' }}
                          fill={
                            pathname.startsWith('/brands')
                              ? 'secondary.600'
                              : 'text'
                          }
                          alignItems="center"
                        >
                          <ArrowDown
                            fill={
                              pathname.startsWith('/brands')
                                ? 'secondary.600'
                                : 'text'
                            }
                          />
                        </Flex>
                      }
                      fontWeight={500}
                      variant="unstyled"
                    >
                      {t('brands')}
                    </MenuButton>
                    <MenuList
                      margin={0}
                      padding={0}
                      display={'flex'}
                      borderRadius={'11px'}
                      zIndex={'dropdown'}
                      height={brands && `${brands?.length * 49 + 50}px`}
                      minHeight={'280px'}
                      w="100%"
                    >
                      <Box margin={0} padding={0} pt={'21px'}>
                        {brands?.map((item) => (
                          <MenuItem
                            as={CkLink}
                            key={item.title}
                            href={routes.brand.href(item.code)}
                            paddingRight={'10px'}
                            w={'240px'}
                            mr={'36px'}
                            pt={'12px'}
                            pb={'13px'}
                            color={'text'}
                            bgColor={
                              item.title === brand
                                ? 'secondary.100'
                                : 'transparent'
                            }
                            cursor={'pointer'}
                            onMouseEnter={() => setBrand(item.title)}
                          >
                            <Box mr={'10px'}>{item.title}</Box>
                          </MenuItem>
                        ))}
                      </Box>
                      <Box
                        minHeight="280px"
                        height={brands && `${brands?.length * 45 + 50}px`}
                        paddingBlock="21px"
                      >
                        <Divider orientation="vertical" />
                      </Box>
                      <Stack
                        direction="column"
                        flexWrap="wrap"
                        margin={0}
                        padding={0}
                        pt={'21px'}
                        minWidth="338px"
                        gap="0"
                      />
                      {brands?.map(
                        (item) =>
                          item?.title === brand && (
                            <Box
                              key={item.title}
                              height="auto"
                              aspectRatio={422 / 188}
                              margin={'21px 21px 21px 23px'}
                              display={'flex'}
                              position="relative"
                              alignItems={'center'}
                              sx={{
                                img: {
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                },
                              }}
                            >
                              <Image
                                src={item.banner_image || ''}
                                alt={brand}
                                fill
                              />
                            </Box>
                          )
                      )}
                    </MenuList>
                  </Menu>
                )}
                <Flex align="center">
                  {internalLinks.map((internalLink) => (
                    <CkLink
                      color={
                        pathname === internalLink.url ? 'secondary.600' : 'text'
                      }
                      fontWeight={500}
                      key={internalLink.url}
                      href={internalLink.url}
                      px={5}
                      height="min-content"
                      _hover={{ color: 'secondary.600' }}
                    >
                      {internalLink.title}
                    </CkLink>
                  ))}
                </Flex>
              </Box>
            </Flex>
            <HStack alignItems={'center'} gap={6}>
              <HeaderSearch />
              <DesktopCart />
              <HeaderProfile />
            </HStack>
          </Flex>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeaderDesktop;
